import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, BoxIcon, SearchIcon, FilterIcon, EditIcon, TrashIcon, EyeIcon, AlertCircleIcon, CalendarIcon, PackageIcon, PaletteIcon, BriefcaseIcon, FileTextIcon, CopyIcon, MoreVerticalIcon, CheckCircleIcon, ClockIcon, XCircleIcon, RefreshCwIcon, BellIcon, PlusIcon, ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// --- Types ---
type ProductType = 'book' | 'merch' | 'event' | 'bookbox' | 'service' | 'creative' | 'article';
type ProductStatus = 'published' | 'draft' | 'flagged' | 'under_review' | 'processing' | 'scheduled' | 'unpublished';
type UploadStatus = 'ready' | 'processing_cover' | 'metadata_review' | 'upload_failed';
interface Listing {
  id: string;
  title: string;
  type: ProductType;
  image: string;
  price: number | null;
  sales: number;
  status: ProductStatus;
  uploadStatus: UploadStatus;
  flagReason?: string;
  date: string;
}
// --- Mock Data ---
const mockListings: Listing[] = [{
  id: '1',
  title: 'The Silent Echo',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 14.99,
  sales: 128,
  status: 'published',
  uploadStatus: 'ready',
  date: '2023-10-15'
}, {
  id: '2',
  title: 'Echo Series T-Shirt',
  type: 'merch',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 24.99,
  sales: 76,
  status: 'published',
  uploadStatus: 'processing_cover',
  date: '2023-11-02'
}, {
  id: '3',
  title: 'Whispers in the Dark',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 12.99,
  sales: 0,
  status: 'draft',
  uploadStatus: 'ready',
  date: '2023-12-01'
}, {
  id: '4',
  title: 'Midnight Launch Party',
  type: 'event',
  image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 0,
  sales: 45,
  status: 'scheduled',
  uploadStatus: 'ready',
  date: '2024-02-14'
}, {
  id: '5',
  title: 'Fantasy Lovers Box',
  type: 'bookbox',
  image: 'https://images.unsplash.com/photo-1549880338-65ddcdfd017b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 49.99,
  sales: 12,
  status: 'under_review',
  uploadStatus: 'metadata_review',
  date: '2024-01-20'
}, {
  id: '6',
  title: 'Editing Services',
  type: 'service',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: null,
  sales: 8,
  status: 'published',
  uploadStatus: 'ready',
  date: '2023-09-10'
}, {
  id: '7',
  title: 'Cover Design Portfolio',
  type: 'creative',
  image: 'https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: null,
  sales: 0,
  status: 'published',
  uploadStatus: 'ready',
  date: '2023-08-05'
}, {
  id: '8',
  title: 'How to Write a Bestseller',
  type: 'article',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 0,
  sales: 0,
  status: 'draft',
  uploadStatus: 'ready',
  date: '2024-01-15'
}, {
  id: '9',
  title: 'Forbidden Romance Bundle',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 9.99,
  sales: 0,
  status: 'flagged',
  uploadStatus: 'upload_failed',
  flagReason: 'Cover image resolution too low',
  date: '2024-01-10'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: ProductStatus;
}) => {
  const styles = {
    published: 'bg-green-100 text-green-800 border-green-200',
    draft: 'bg-gray-100 text-gray-800 border-gray-200',
    flagged: 'bg-red-100 text-red-800 border-red-200',
    under_review: 'bg-amber-100 text-amber-800 border-amber-200',
    processing: 'bg-blue-100 text-blue-800 border-blue-200',
    scheduled: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    unpublished: 'bg-gray-100 text-gray-500 border-gray-200'
  };
  const labels = {
    published: 'Published',
    draft: 'Draft',
    flagged: 'Flagged',
    under_review: 'In Review',
    processing: 'Processing',
    scheduled: 'Scheduled',
    unpublished: 'Unpublished'
  };
  const rt = useRoleTheme();
  return <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium border ${styles[status]}`} data-id="element-9603" data-mp="2xvg94i4pn9qb">
      {status === 'flagged' && <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-9604" data-mp="3chcag7e2k5q6" />}
      {status === 'processing' && <RefreshCwIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-9605" data-mp="3hh8f88ulc16s" />}
      {status === 'scheduled' && <ClockIcon className="w-3 h-3 mr-1" data-id="element-9606" data-mp="33h7bmlmxyfnm" />}
      {labels[status]}
    </span>;
};
const TypeBadge = ({
  type
}: {
  type: ProductType;
}) => {
  const styles = {
    book: 'bg-purple-100 text-purple-800',
    merch: 'bg-amber-100 text-amber-800',
    event: 'bg-teal-100 text-teal-800',
    bookbox: 'bg-pink-100 text-pink-800',
    service: 'bg-blue-100 text-blue-800',
    creative: 'bg-violet-100 text-violet-800',
    article: 'bg-emerald-100 text-emerald-800'
  };
  const icons = {
    book: BookIcon,
    merch: BoxIcon,
    event: CalendarIcon,
    bookbox: PackageIcon,
    service: BriefcaseIcon,
    creative: PaletteIcon,
    article: FileTextIcon
  };
  const Icon = icons[type];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[type]}`} data-id="element-9607" data-mp="2fruvt2x3z9cc">
      <Icon className="w-3 h-3 mr-1" data-id="element-9608" data-mp="lc69udim4t0t" />
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>;
};
const UploadStatusIndicator = ({
  status
}: {
  status: UploadStatus;
}) => {
  if (status === 'ready') return <span className="text-gray-400 text-xs flex items-center" data-id="element-9609" data-mp="blurvn23gjax">
        <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-9610" data-mp="7kizxavqzlzk" /> Ready
      </span>;
  if (status === 'processing_cover') return <span className="text-blue-600 text-xs flex items-center" data-id="element-9611" data-mp="148p8g8vsiyj4">
        <RefreshCwIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-9612" data-mp="zae3ockcre7r" /> Processing
        cover...
      </span>;
  if (status === 'metadata_review') return <span className="text-amber-600 text-xs flex items-center" data-id="element-9613" data-mp="vbeyqrymfhgb">
        <ClockIcon className="w-3 h-3 mr-1" data-id="element-9614" data-mp="q9fulfv24gib" /> Metadata review
      </span>;
  if (status === 'upload_failed') return <span className="text-red-600 text-xs flex items-center" data-id="element-9615" data-mp="3fxgiw2i58asp">
        <XCircleIcon className="w-3 h-3 mr-1" data-id="element-9616" data-mp="3azey9d52k3f4" /> Upload failed
      </span>;
  return null;
};
const ManageListings = () => {
  const dk = useDarkClasses();
  const [activeType, setActiveType] = useState<ProductType | 'all'>('all');
  const [activeStatus, setActiveStatus] = useState<ProductStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter logic
  const filteredListings = mockListings.filter(item => {
    const matchesType = activeType === 'all' || item.type === activeType;
    const matchesStatus = activeStatus === 'all' || item.status === activeStatus;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });
  // Stats
  const stats = {
    total: mockListings.length,
    published: mockListings.filter(i => i.status === 'published').length,
    drafts: mockListings.filter(i => i.status === 'draft').length,
    flagged: mockListings.filter(i => i.status === 'flagged').length
  };
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-9617" data-mp="swgsc5okr44y">
      {/* Alert Banner */}
      {stats.flagged > 0 && <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-id="element-9618" data-mp="4zk7w72jpgy4">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-9619" data-mp="4x7r3x8ixcmn">
            <div className="flex items-center text-amber-800" data-id="element-9620" data-mp="ipxh0cr6b1p1">
              <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-9621" data-mp="eovftqdp4fkf" />
              <span className="text-sm font-medium" data-id="element-9622" data-mp="erhmzowoct24">
                {stats.flagged} listing{stats.flagged > 1 ? 's' : ''} require
                {stats.flagged === 1 ? 's' : ''} your attention.
              </span>
            </div>
            <button onClick={() => setActiveStatus('flagged')} className="text-sm font-medium text-amber-900 underline hover:text-amber-700" data-id="element-9623" data-mp="hexkzub16i">
              Review
            </button>
          </div>
        </div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9624" data-mp="1bh7dchy0uopk">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-9625" data-mp="16idxwjz6cnmr">
          <div data-id="element-9626" data-mp="1ezeljot1w1a0">
            <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-9627" data-mp="1iyx6ulosaedd">
              Manage Listings
            </h1>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-9628" data-mp="14zdzvw7pl92n">
              Track and manage all your published products
            </p>
          </div>
          <Link to="/vendors/product-manager" className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-9629" data-mp="19yqux48qhmgb">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-9630" data-mp="1nw5nuiy74cd1" />
            Add New Listing
          </Link>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-9631" data-mp="2gmdzdry87yin">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-9632" data-mp="2lddv4tvm0ajk">
            <div className="flex items-center justify-between" data-id="element-9633" data-mp="2lfgw28yavo32">
              <div data-id="element-9634" data-mp="16wd155mpftpj">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9635" data-mp="16vtkds8sjbd3">
                  Total Listings
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-9636" data-mp="xttxijb3hd28">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-9637" data-mp="o4skn1zujbsh">
                <PackageIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-9638" data-mp="o3902z75qekw" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-9639" data-mp="sxllhes71zza">
            <div className="flex items-center justify-between" data-id="element-9640" data-mp="f5p1jy0oiwdz">
              <div data-id="element-9641" data-mp="j4xuj0nwaq8h">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9642" data-mp="j6iq8tly4f7x">
                  Published
                </p>
                <p className={`text-2xl font-bold mt-1 text-green-600`} data-id="element-9643" data-mp="a5arybjfjedu">
                  {stats.published}
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-9644" data-mp="3mqpw9a1ok21i">
                <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-9645" data-mp="3hqlpibqkqloh" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-9646" data-mp="izzbof1m53w">
            <div className="flex items-center justify-between" data-id="element-9647" data-mp="3rmooj8q4q477">
              <div data-id="element-9648" data-mp="9hturbgn0pfv">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9649" data-mp="9hborn46awur">
                  Drafts
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-9650" data-mp="j7n6qdkoz6kn">
                  {stats.drafts}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-9651" data-mp="e9bwi1qhiu1w">
                <EditIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-9652" data-mp="s837vgdyq1mc" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-9653" data-mp="o812ox90qq2b">
            <div className="flex items-center justify-between" data-id="element-9654" data-mp="vaptj2ogng93">
              <div data-id="element-9655" data-mp="v83e6gvbvwgb">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9656" data-mp="109145fmkxusd">
                  Needs Attention
                </p>
                <p className={`text-2xl font-bold mt-1 text-amber-600`} data-id="element-9657" data-mp="156j26az26pll">
                  {stats.flagged}
                </p>
              </div>
              <div className="p-2 rounded-full bg-amber-100" data-id="element-9658" data-mp="1dznrkbmw3y95">
                <AlertCircleIcon className="h-5 w-5 text-amber-600" data-id="element-9659" data-mp="1izs7o0jvyqyu" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-9660" data-mp="1nzmzc6bhjkky">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between" data-id="element-9661" data-mp="1svvli3o3oe1m">
            {/* Type Tabs */}
            <div className="w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-9662" data-mp="3up1jf57rd7n7">
              <div className="flex space-x-2" data-id="element-9663" data-mp="2btygrclhclsg">
                {['all', 'book', 'merch', 'event', 'bookbox', 'service', 'creative', 'article'].map(type => <button key={type} onClick={() => setActiveType(type as ProductType | 'all')} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeType === type ? 'bg-purple-100 text-purple-700' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-9664" data-mp="1y3lbjhzz3l6t">
                    {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1) + (type === 'merch' ? '' : 's')}
                  </button>)}
              </div>
            </div>

            {/* Right Side: Status & Search */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-9665" data-mp="2ta11hsawsmh8">
              <div className="relative min-w-[160px]" data-id="element-9666" data-mp="2y1sqvzibwm5s">
                <select value={activeStatus} onChange={e => setActiveStatus(e.target.value as ProductStatus | 'all')} className={`w-full pl-3 pr-8 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-9667" data-mp="2y3dpc07rkp8x">
                  <option value="all" data-id="element-9668" data-mp="3gyaqf33f5qcl">All Statuses</option>
                  <option value="published" data-id="element-9669" data-mp="32zkn35q0aufs">Published</option>
                  <option value="draft" data-id="element-9670" data-mp="37zgp4zf58bsp">Draft</option>
                  <option value="flagged" data-id="element-9671" data-mp="37y5m51tiv82n">Flagged</option>
                  <option value="under_review" data-id="element-9672" data-mp="1rkxjr77u3q8b">In Review</option>
                  <option value="processing" data-id="element-9673" data-mp="1rntiuyvf4kyc">Processing</option>
                  <option value="scheduled" data-id="element-9674" data-mp="1wlcvjucgwpo6">Scheduled</option>
                  <option value="unpublished" data-id="element-9675" data-mp="1p0smodhju5dt">Unpublished</option>
                </select>
                <ChevronDownIcon className={`absolute right-2.5 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-9676" data-mp="1k27u908jgpzx" />
              </div>

              <div className="relative flex-grow sm:max-w-xs" data-id="element-9677" data-mp="1xzmm0bm9l9v2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9678" data-mp="1t3xkw3uljkwn">
                  <SearchIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-9679" data-mp="15duxi11mn34d" />
                </div>
                <input type="text" placeholder="Search listings..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-9680" data-mp="11cho5gzbshsp" />
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredListings.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-9681" data-mp="2mo1jttdi6p3k">
            <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-9682" data-mp="2rg1p81ccea7j" />
            <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-9683" data-mp="2wdl6la4c733p">
              No listings found
            </h3>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-9684" data-mp="1ysfg6dkcidda">
              Try adjusting your search or filters.
            </p>
            <div className="mt-6" data-id="element-9685" data-mp="23rs8vexweens">
              <button onClick={() => {
            setActiveType('all');
            setActiveStatus('all');
            setSearchQuery('');
          }} className="text-sm font-medium text-purple-600 hover:text-purple-500" data-id="element-9686" data-mp="28osghuaebph0">
                Clear all filters
              </button>
            </div>
          </div> : <>
            {/* Desktop Table View */}
            <div className={`hidden md:block rounded-lg shadow-sm border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-9687" data-mp="1ckjny8n074s9">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-9688" data-mp="13plw74vldy2i">
                <thead className={dk.bgMuted} data-id="element-9689" data-mp="yrkaec6s7knc">
                  <tr data-id="element-9690" data-mp="yqse0z5d1iw0">
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9691" data-mp="yp7tm2crfgbv">
                      Product
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9692" data-mp="ksaxpmhsb8o4">
                      Type
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9693" data-mp="2f2xyvcfm8an3">
                      Status
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9694" data-mp="1nymzczwjp61f">
                      Upload Status
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9695" data-mp="10alvw5e84fsv">
                      Price
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-9696" data-mp="qdqyxqxlmngp">
                      Sales
                    </th>
                    <th scope="col" className={`relative px-6 py-3`} data-id="element-9697" data-mp="2pp1v0dms4qf6">
                      <span className="sr-only" data-id="element-9698" data-mp="2pn7innpkq6mp">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${dk.card}`} data-id="element-9699" data-mp="933vw6jtoj6f">
                  {filteredListings.map(listing => <tr key={listing.id} className={`hover:${dk.bgMuted}/50 transition-colors`} data-id="element-9700" data-mp="j11k4u1omfz1">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9701" data-mp="wqv9ddh3n29a">
                        <div className="flex items-center" data-id="element-9702" data-mp="ru4uuuky0epz">
                          <div className="h-10 w-10 flex-shrink-0" data-id="element-9703" data-mp="16qm2baq0h979">
                            <img className="h-10 w-10 rounded object-cover" src={listing.image} alt="" data-id="element-9704" data-mp="16pui0w1v6xwa" />
                          </div>
                          <div className="ml-4" data-id="element-9705" data-mp="33jfgzn6crj55">
                            <div className={`text-sm font-medium ${dk.textHeading}`} data-id="element-9706" data-mp="33kqkrmzssqw9">
                              {listing.title}
                            </div>
                            {listing.flagReason && <div className="text-xs text-red-500 mt-0.5" data-id="element-9707" data-mp="1a7bpos7kzget">
                                {listing.flagReason}
                              </div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9708" data-mp="clnz4nxt3sb7">
                        <TypeBadge type={listing.type} data-id="element-9709" data-mp="ri5d7mxdrl1g" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9710" data-mp="rfslii1tlwb7">
                        <StatusBadge status={listing.status} data-id="element-9711" data-mp="mhhba67m5jsg" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9712" data-mp="22vzdovjpsjw2">
                        <UploadStatusIndicator status={listing.uploadStatus} data-id="element-9713" data-mp="1xyhazmwaj0wi" />
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-9714" data-mp="25h7403usoj9n">
                        {listing.price !== null ? `$${listing.price.toFixed(2)}` : '—'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-9715" data-mp="20hvnmvycbfsl">
                        {listing.sales}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9716" data-mp="2t69mtf160i5q">
                        <div className="flex items-center justify-end space-x-3" data-id="element-9717" data-mp="2feuxyrkktmqr">
                          <button className={`text-gray-400 hover:${dk.textHeading}`} title="View" data-id="element-9718" data-mp="2jgq1m2pzcc7p">
                            <EyeIcon className="h-4 w-4" data-id="element-9719" data-mp="3bxkbdpjf4iug" />
                          </button>
                          <Link to={`/vendors/product-manager?type=${listing.type}&id=${listing.id}&returnTo=/manage-listings`} className="text-purple-600 hover:text-purple-900" title="Edit" data-id="element-9720" data-mp="3ltj20461cb1e">
                            <EditIcon className="h-4 w-4" data-id="element-9721" data-mp="37u9urtzt4r9k" />
                          </Link>
                          <button className={`text-gray-400 hover:${dk.textHeading}`} title="Duplicate" data-id="element-9722" data-mp="32uoloqqxb2j4">
                            <CopyIcon className="h-4 w-4" data-id="element-9723" data-mp="2u2mnz088o0cj" />
                          </button>
                          <button className={`text-gray-400 hover:text-red-500`} title="Delete" data-id="element-9724" data-mp="2u08totldus61">
                            <TrashIcon className="h-4 w-4" data-id="element-9725" data-mp="2k3nn43tpt903" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4" data-id="element-9726" data-mp="29ynro0e25flf">
              {filteredListings.map(listing => <div key={listing.id} className={`relative rounded-lg shadow-sm border p-4 ${dk.card} ${dk.border} ${listing.status === 'flagged' ? 'border-l-4 border-l-red-500' : ''}`} data-id="element-9727" data-mp="2f0d2bexdbr8z">
                  <div className="flex gap-4" data-id="element-9728" data-mp="1r9r5kjstdtea">
                    <div className="flex-shrink-0" data-id="element-9729" data-mp="1raiq8zkvi6ps">
                      <img src={listing.image} alt="" className={`rounded object-cover ${listing.type === 'book' ? 'w-14 h-[72px]' : 'w-14 h-14'}`} data-id="element-9730" data-mp="1w8kbnr5rui4f" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-9731" data-mp="13ilt8em31j8q">
                      <div className="flex justify-between items-start" data-id="element-9732" data-mp="18iho9ocsy0c5">
                        <div data-id="element-9733" data-mp="3a85mbuceceta">
                          <h3 className={`text-sm font-medium truncate pr-2 ${dk.textHeading}`} data-id="element-9734" data-mp="3a9gppt1xjkjv">
                            {listing.title}
                          </h3>
                          <div className="mt-1 flex items-center gap-2" data-id="element-9735" data-mp="3k0l4arnp13e6">
                            <TypeBadge type={listing.type} data-id="element-9736" data-mp="3f705ixea05dx" />
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-9737" data-mp="3sxddv0v2xm3s">
                          <button className={`p-1 rounded-full hover:${dk.bgMuted} ${dk.textMuted}`} data-id="element-9738" data-mp="3owjmtymcbr6o">
                            <MoreVerticalIcon className="h-4 w-4" data-id="element-9739" data-mp="6pm13bs4y97z" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2 items-center" data-id="element-9740" data-mp="28k4o2w4beiq9">
                        <StatusBadge status={listing.status} data-id="element-9741" data-mp="23la43qum2o4k" />
                        <UploadStatusIndicator status={listing.uploadStatus} data-id="element-9742" data-mp="j27nehvykq9y" />
                      </div>

                      {listing.flagReason && <p className="mt-2 text-xs text-red-600 flex items-center" data-id="element-9743" data-mp="o0rc2q1xsg60">
                          <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-9744" data-mp="9bd4nnhx88t9" />
                          {listing.flagReason}
                        </p>}

                      <div className="mt-3 flex items-center justify-between border-t pt-3 dark:border-gray-700" data-id="element-9745" data-mp="bzpqfb12s15">
                        <div className={`text-xs ${dk.textMuted}`} data-id="element-9746" data-mp="5b2wc46np6b8">
                          <span className={`font-medium ${dk.textHeading}`} data-id="element-9747" data-mp="3miy01fm1q1km">
                            {listing.price !== null ? `$${listing.price.toFixed(2)}` : '—'}
                          </span>
                          <span className="mx-2" data-id="element-9748" data-mp="1ks2zybkt3g83">•</span>
                          <span data-id="element-9749" data-mp="1pr6844w322ka">{listing.sales} sales</span>
                        </div>
                        <Link to={`/vendors/product-manager?type=${listing.type}&id=${listing.id}&returnTo=/manage-listings`} className="text-xs font-medium text-purple-600 hover:text-purple-500 flex items-center" data-id="element-9750" data-mp="1o97krkhq18hd">
                          <EditIcon className="h-3 w-3 mr-1" data-id="element-9751" data-mp="1a9psgv57vd39" />
                          Edit
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </>}
      </div>
    </div>;
};
export default ManageListings;